import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
} from '@mui/material';
import {
  Speed,
  Security,
  Public,
  CheckCircle,
  ArrowForward,
  Router,
  Wifi,
  Business,
  CellTower,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import CTASection from '../components/common/CTASection';

const FiberNetwork = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Consistent stats matching home page
  const stats = [
    { number: '500,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '10,000+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '150+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <CheckCircle />, color: '#191919' },
  ];

  const features = [
    { icon: <Speed />, title: 'Lightning Fast', desc: 'Up to 100Gbps dedicated connectivity', color: '#e60108' },
    { icon: <Security />, title: 'Secure Network', desc: 'End-to-end encryption and security', color: '#191919' },
    { icon: <Public />, title: 'Global Reach', desc: '500,000+ km network across 150+ countries', color: '#e60108' },
    { icon: <Wifi />, title: 'Low Latency', desc: 'Sub-5ms latency between major hubs', color: '#a0a0a0' },
  ];

  const routes = [
    { name: 'Trans-Atlantic Express', capacity: '100Tbps', latency: '45ms', status: 'Operational', color: '#e60108' },
    { name: 'Asia Pacific Gateway', capacity: '80Tbps', latency: '65ms', status: 'Operational', color: '#191919' },
    { name: 'Europe-Middle East Link', capacity: '60Tbps', latency: '35ms', status: 'Operational', color: '#e60108' },
    { name: 'Pan-American Network', capacity: '120Tbps', latency: '40ms', status: 'Expanding', color: '#a0a0a0' },
  ];

  return (
    <Box>
      {/* Hero Section with Image */}
      <Box
        sx={{
          position: 'relative',
          height: '50vh',
          minHeight: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src="/images/Fiber Optic Network.png"
          alt="Fiber Optic Network"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />
        
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1,
          }}
        />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Chip
              label="Fiber Network"
              icon={<Business sx={{ fontSize: 16 }} />}
              sx={{
                bgcolor: '#e60108',
                color: '#fefefe',
                mb: 3,
                fontWeight: 700,
                letterSpacing: '0.5px',
                mx: 'auto',
                '& .MuiChip-icon': { color: '#fefefe' },
              }}
            />
            <Typography
              variant="h1"
              sx={{
                color: '#fefefe',
                mb: 2,
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                lineHeight: 1.2,
                textShadow: '0 2px 20px rgba(0,0,0,0.3)',
              }}
            >
              Fiber Optic Network
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(254,254,254,0.9)',
                maxWidth: 700,
                mx: 'auto',
                fontSize: '1.25rem',
                lineHeight: 1.6,
              }}
            >
              Light-speed connectivity across continents with 500,000+ km of fiber infrastructure
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section - No Image, Centered */}
      <Box sx={{ py: 10, bgcolor: '#fefefe' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip 
              label="Fiber Network" 
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
              Enterprise-Grade Fiber Solutions
            </Typography>
            <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 5, lineHeight: 1.8, maxWidth: 800, mx: 'auto' }}>
              Our fiber optic network provides the backbone for global communication, 
              offering unparalleled speed, reliability, and security for businesses worldwide.
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {features.map((feature, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -5 }}
                  >
                    <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3, height: '100%' }}>
                      <Avatar sx={{ bgcolor: feature.color + '15', color: feature.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                        {feature.icon}
                      </Avatar>
                      <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                        {feature.desc}
                      </Typography>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/contact')}
                sx={{ mt: 5, bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
              >
                Get Connected
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Global Routes Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center' }}
          >
            <Chip
              label="Global Network"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Global Network Routes
            </Typography>
            <Typography variant="h6" sx={{ color: '#a0a0a0', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Strategic connectivity between major business hubs
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {routes.map((route, index) => (
              <Grid item xs={12} md={6} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Card sx={{ p: 3, borderRadius: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Router sx={{ color: route.color }} />
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#191919' }}>
                          {route.name}
                        </Typography>
                      </Stack>
                      <Chip 
                        label={route.status} 
                        size="small"
                        sx={{ 
                          bgcolor: route.status === 'Operational' ? '#00C853' : '#FFC107',
                          color: '#fefefe',
                          fontWeight: 600,
                        }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={3}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#a0a0a0' }}>Capacity</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#191919' }}>{route.capacity}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#a0a0a0' }}>Latency</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#191919' }}>{route.latency}</Typography>
                      </Box>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Light Up Your Connectivity" 
        subtitle="Get dedicated fiber connectivity for your business"
        buttonText="Get Quote"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default FiberNetwork;